import { useCallback, useState } from 'react';

/** Au-delà, l'historique cesse d'être un raccourci : les entrées les plus anciennes sortent. */
const MAX_ENTRIES = 8;

function read(storageKey: string): string[] {
    try {
        const raw = localStorage.getItem(storageKey);
        const parsed: unknown = raw ? JSON.parse(raw) : [];

        return Array.isArray(parsed) ? parsed.filter((value): value is string => typeof value === 'string') : [];
    } catch {
        // Stockage illisible (quota, navigation privée, valeur corrompue) : l'historique n'est pas
        // essentiel, on repart d'une liste vide plutôt que de casser la recherche.
        return [];
    }
}

/**
 * Dernières recherches de l'utilisateur, conservées dans le navigateur.
 *
 * On rouvre souvent le même dossier dans la journée, en retapant le même numéro ou le même nom.
 */
export function useSearchHistory(storageKey: string) {
    const [entries, setEntries] = useState<string[]>(() => read(storageKey));

    const remember = useCallback(
        (term: string) => {
            const value = term.trim();
            if (value === '') return;

            setEntries((current) => {
                // La saisie remonte en tête, sans doublon (comparaison insensible à la casse).
                const next = [value, ...current.filter((entry) => entry.toLowerCase() !== value.toLowerCase())].slice(0, MAX_ENTRIES);

                try {
                    localStorage.setItem(storageKey, JSON.stringify(next));
                } catch {
                    // Écriture impossible : l'historique reste vivant le temps de la session.
                }

                return next;
            });
        },
        [storageKey],
    );

    const clear = useCallback(() => {
        setEntries([]);
        try {
            localStorage.removeItem(storageKey);
        } catch {
            // Rien à faire de plus : la liste affichée est déjà vide.
        }
    }, [storageKey]);

    return { entries, remember, clear };
}
